import React, { useState } from 'react';
import {Helmet} from "react-helmet";
import SiteHeader from "../components/SiteHeader";
import ImageLightbox from "../components/ImageLightbox";
import ContactModal from "../components/ContactModal";
import useFetch from "../hooks/useFetch";
import LoginTabs from "./LoginTabs";

const LoginPage = (properties) => {
    const { loading, error, data } = useFetch("/login");
    const [contactOpen, setContactOpen] = useState(false);
    const [contactSource, setContactSource] = useState("");
    const openContact = (src) => {
        setContactSource(src);
        setContactOpen(true);
    };
    const linkStyle = {
        background: "none",
        border: "none",
        padding: 0,
        cursor: "pointer",
    };

    if (error) return <p>Error</p>;
    if (loading)
        return (
            <div className="d-flex justify-content-center mt-5 pt-lg-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );

    let content = data.data.attributes;

    return (
        <div>
            <Helmet>
                <meta name="description" content={content.metadesc}/>
                <meta property="og:title" content={content.metatitle}/>
                <meta property="og:description" content={content.metadesc}/>
                <title>{content.metatitle}</title>
            </Helmet>

            {/* Hero CTA Buttons */}
            <style>{`
              .btn-login-hero {
                background-color: #ffffff !important;
                border: 2px solid #b8dff0 !important;
                color: #1B3A5F !important;
                transition: background-color 0.2s ease, border-color 0.2s ease;
              }
              .btn-login-hero:hover,
              .btn-login-hero:focus {
                background-color: transparent !important;
                border-color: #b8dff0 !important;
                color: #1B3A5F !important;
              }
              .btn-login-secondary {
                background-color: transparent !important;
                border: 2px solid #b8dff0 !important;
                color: #1B3A5F !important;
                transition: background-color 0.2s ease;
              }
              .btn-login-secondary:hover,
              .btn-login-secondary:focus {
                background-color: #ffffff !important;
                color: #1B3A5F !important;
              }
            `}</style>

            <SiteHeader
                title={content.title}
                eyebrow={content.eyebrow}
                bgimage={`${content.bgimage}`}
            >
                <p className="text-secondary fw-light mb-3">
                    Die Benutzerdaten erhalten Sie per Mail bei der Übergabe eines Inkasso-Falles. Nicht passiert? <a href="/kontakt" className="text-info">Kontaktieren</a> Sie uns.
                </p>
                <a
                    href="https://login.inkasso-partner.ch/IKLU/"
                    target="_blank"
                    rel="noreferrer"
                    className="btn fw-bold rounded-3 btn-login-hero me-2 mb-2 mb-sm-0"
                >
                    Login neues webINKASSO
                </a>
                <a
                    href="#mehr-als-inkasso"
                    className="btn fw-bold rounded-3 btn-login-secondary"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const el = document.getElementById("mehr-als-inkasso");
                        if (!el) return;
                        const startY = window.pageYOffset;
                        const targetY = el.getBoundingClientRect().top + startY - 60;
                        const distance = targetY - startY;
                        if (Math.abs(distance) < 1) return;
                        // Manual smooth animation via RAF — robust across Safari and Chrome,
                        // independent of prefers-reduced-motion.
                        const duration = 700;
                        const startTs = performance.now();
                        // easeOutQuart for snappy start and gentle landing
                        const ease = (t) => 1 - Math.pow(1 - t, 4);
                        const step = () => {
                            const elapsed = performance.now() - startTs;
                            const progress = Math.min(elapsed / duration, 1);
                            window.scrollTo(0, startY + distance * ease(progress));
                            if (progress < 1) requestAnimationFrame(step);
                        };
                        requestAnimationFrame(step);
                        // Safety net only for the case where RAF never ticks (background tab).
                        // Triggers well after the animation would have finished.
                        setTimeout(() => {
                            if (Math.abs(window.pageYOffset - targetY) > 30) {
                                window.scrollTo(0, targetY);
                            }
                        }, 1500);
                    }}
                >
                    Mehr als Inkasso
                </a>
            </SiteHeader>

            <main className="container pt-lg-3 mt-lg-3">

                {/* ===== Vorteile webINKASSO Tabs ===== */}
                <div className="row position-relative">
                    <div className="col-lg-7 offset-xl-1 text-start">
                        <h2 className="text-secondary fw-bold mt-2 mt-lg-3 mb-3 mb-lg-4">Vorteile neues webINKASSO</h2>

                        <LoginTabs/>

                        <p className="text-secondary fw-light mb-3">
                            Für eine Live-Demonstration aller Funktionen sowie weitere Informationen kontaktieren Sie Ihren Kundenbetreuer oder schreiben Sie uns direkt per
                            <a href="mailto:inkasso@luzern.creditreform.ch" className="text-info ms-1">Mail</a>.
                        </p>
                    </div>
                </div>

                {/* ===== webINKASSO – umfassendes Inkassoportal (Bild, klickbar) ===== */}
                <div className="row position-relative">
                    <div className="col-lg-11 offset-xl-1 text-start">
                        <h2 className="text-secondary fw-bold mt-4 mt-lg-5 mb-3 mb-lg-4">webINKASSO – umfassendes Inkassoportal</h2>
                        <div className="col-lg-8 col-md-10">
                            <ImageLightbox
                                src={content.image_url}
                                alt="webINKASSO Portalübersicht"
                                thumbClassName="img-fluid w-100 rounded-3 shadow-sm"
                            />
                            <p className="text-secondary fw-light fst-italic mt-2 mb-0" style={{fontSize: "0.85rem"}}>
                                Klicken Sie auf die Übersicht für die Vollansicht.
                            </p>
                        </div>
                    </div>
                </div>

                {/* ===== Mehr als Inkasso: Cross-Sell ===== */}
                <div id="mehr-als-inkasso" className="row position-relative" style={{scrollMarginTop: "80px"}}>
                    <div className="col-lg-11 offset-xl-1 text-start">
                        <h2 className="text-secondary fw-bold mt-5 mt-lg-5 mb-3 mb-lg-4 pt-lg-3">Mehr als Inkasso</h2>
                        <p className="text-secondary fw-light mb-4 col-lg-10">
                            Sie nutzen webINKASSO für die Bearbeitung offener Forderungen. Mit unseren Bonitäts- und Monitoring-Lösungen verhindern Sie Zahlungsausfälle, bevor sie überhaupt entstehen. Tagesaktuelle Daten von über 12'000 Schweizer Mitgliedern, direkt aus Ihrem Account abrufbar.
                        </p>

                        <div className="col-lg-10">
                            {/* Karte 1: Bonitätsauskunft */}
                            <div className="bg-white shadow rounded-3 p-4 p-xl-5 mb-3">
                                <h4 className="text-info fw-bold fs-5 mb-2">Bonitätsauskunft einholen – weltweit</h4>
                                <p className="text-secondary fw-light mb-3">
                                    Auskünfte über Firmen und Privatpersonen online einholen mit der führenden Bonitätsdatenbank der Schweiz.
                                </p>
                                <button type="button" onClick={() => openContact("Bonitätsauskunft")} className="text-info fw-bold text-decoration-none" style={linkStyle}>› Beratung anfragen</button>
                            </div>

                            {/* Karte 2: Debitoren Monitoring */}
                            <div className="bg-white shadow rounded-3 p-4 p-xl-5 mb-3">
                                <h4 className="text-info fw-bold fs-5 mb-2">Debitoren-Monitoring</h4>
                                <p className="text-secondary fw-light mb-3">
                                    Wir liefern Ihnen die Informationen zu relevanten Veränderungen bei Ihrer Kundschaft. Mit dem Monitoring von Creditreform haben Sie Ihre Kunden stets im Blickfeld.
                                </p>
                                <button type="button" onClick={() => openContact("Debitoren-Monitoring")} className="text-info fw-bold text-decoration-none" style={linkStyle}>› Kundenbetreuer kontaktieren</button>
                            </div>

                            {/* Karte 3: ERP-Anbindung */}
                            <div className="bg-white shadow rounded-3 p-4 p-xl-5 mb-3">
                                <h4 className="text-info fw-bold fs-5 mb-2">Anbindung an Ihr ERP</h4>
                                <p className="text-secondary fw-light mb-3">
                                    Bonitätsprüfung via ERP in Microsoft Dynamics, SAP, OPACC oder Comarch. Auch bei Webshops als Plugin für Magento, Shopware und WooCommerce.
                                </p>
                                <button type="button" onClick={() => openContact("ERP-Anbindung")} className="text-info fw-bold text-decoration-none" style={linkStyle}>› Beratung anfragen</button>
                            </div>

                            {/* Persönlich-beraten-Box */}
                            <div className="bg-white shadow rounded-3 p-4 p-xl-5 mb-5 mt-4 border-start border-info border-4">
                                <h4 className="text-info fw-bold fs-5 mb-2">Persönlich beraten lassen</h4>
                                <p className="text-secondary fw-light mb-3">
                                    Für eine Live-Demo aller Möglichkeiten oder konkrete Tarifauskünfte melden Sie sich bei Ihrem Kundenbetreuer oder schreiben Sie direkt an <a href="mailto:vk@luzern.creditreform.ch" className="text-info">vk@luzern.creditreform.ch</a>. Telefonisch erreichen Sie uns Montag bis Freitag unter <a href="tel:+41413701944" className="text-info">+41 41 370 19 44</a>.
                                </p>
                                <a href="mailto:vk@luzern.creditreform.ch" className="text-info fw-bold text-decoration-none">› vk@luzern.creditreform.ch</a>
                            </div>
                        </div>
                    </div>
                </div>

            </main>

            <ContactModal
                open={contactOpen}
                onClose={() => setContactOpen(false)}
                source={contactSource}
            />
        </div>
    );
};

export default LoginPage;
